// calc.ts — single calculation path for the standalone "Calculadora Modelo 303" page (F15b).
//
// SAME logic as the in-app estimator (FreelancerTaxEstimatorView.tsx + freelancerTaxBase.ts):
// amounts are VAT-INCLUDED and the VAT share is extracted at the selected rate
// (`amount × r / (1 + r)`, exactly like computeFreelancerTaxSummary), and the result to pay is
// floored at 0 like the view's `modelo303Result`. Rates come from IVA_RATES_2025 (the app's
// 2025 VAT table) — never redeclared here. Parity test locks both.

import { IVA_RATES_2025 } from '../../features/irpf/data/ivaRates2025';

export interface VatRateOption {
    /** Percentage on the 0-100 scale, straight from IVA_RATES_2025. */
    value: number;
    label: string;
}

/** Selectable rates — values are the app's IVA_RATES_2025 entries (test-locked). */
export const MODELO_303_RATE_OPTIONS: VatRateOption[] = [
    { value: IVA_RATES_2025.general, label: `General (${IVA_RATES_2025.general} %)` },
    { value: IVA_RATES_2025.reduced, label: `Reducido (${IVA_RATES_2025.reduced} %)` },
    { value: IVA_RATES_2025.superReduced, label: `Superreducido (${IVA_RATES_2025.superReduced} %)` },
    { value: IVA_RATES_2025.exempt, label: `Exento (${IVA_RATES_2025.exempt} %)` },
];

export interface Modelo303Input {
    /** Quarter income, VAT INCLUDED, whole euros. */
    grossIncomeWithVat: number;
    /** VAT rate charged on sales (0-100). */
    incomeRatePct: number;
    /** Deductible quarter expenses, VAT INCLUDED, whole euros. */
    deductibleExpensesWithVat: number;
    /** VAT rate borne on purchases (0-100). */
    expenseRatePct: number;
}

export interface Modelo303Estimate {
    ivaRepercutido: number;
    ivaSoportado: number;
    /** Estimated amount to pay, floored at 0 (same as the in-app `modelo303Result`). */
    result: number;
}

/** VAT share of a VAT-included amount — identical extraction to computeFreelancerTaxSummary. */
const extractVat = (grossWithVat: number, ratePct: number): number => {
    const rate = ratePct / 100;
    return rate > 0 ? (grossWithVat * rate) / (1 + rate) : 0;
};

/** Orientative Modelo 303 estimate — same arithmetic as the in-app estimator. */
export function computeModelo303Estimate(input: Modelo303Input): Modelo303Estimate {
    const ivaRepercutido = extractVat(input.grossIncomeWithVat, input.incomeRatePct);
    const ivaSoportado = extractVat(input.deductibleExpensesWithVat, input.expenseRatePct);
    return {
        ivaRepercutido,
        ivaSoportado,
        result: Math.max(0, ivaRepercutido - ivaSoportado),
    };
}
